import { z } from 'zod';
import { normalizeName } from './people.js';

export const planPublicationStates = ['draft', 'published', 'archived'] as const;
export type PlanPublicationState = (typeof planPublicationStates)[number];

export const intensityLevels = ['low', 'medium', 'high'] as const;
export type IntensityLevel = (typeof intensityLevels)[number];

const shortText = (maximum: number) =>
  z.string().transform(normalizeName).pipe(z.string().max(maximum));
const optionalText = (maximum: number) =>
  shortText(maximum)
    .transform((value) => (value === '' ? null : value))
    .nullable();
const titleText = z.string().transform(normalizeName).pipe(z.string().min(1).max(200));

/** A club-authored title in up to three languages; at least one is required and the others fall back to it. */
export const localizedTitlesSchema = z
  .strictObject({
    en: titleText.optional(),
    hu: titleText.optional(),
    ro: titleText.optional(),
  })
  .refine(
    (titles) => titles.en !== undefined || titles.hu !== undefined || titles.ro !== undefined,
  );
export type LocalizedTitles = z.infer<typeof localizedTitlesSchema>;

const drillFields = {
  titles: localizedTitlesSchema,
  protocol: optionalText(2000).default(null),
  instructions: optionalText(5000).default(null),
  durationSeconds: z.number().int().min(1).max(14_400),
  equipment: z
    .array(shortText(100).pipe(z.string().min(1)))
    .max(30)
    .default([]),
};

export const createDrillRequestSchema = z.strictObject({
  /** Null makes the drill available to every department of the club. */
  departmentId: z.uuid().nullable().default(null),
  ...drillFields,
});
export type CreateDrillRequest = z.infer<typeof createDrillRequestSchema>;

/** A new version replaces the whole content; earlier versions stay as they were. */
export const newDrillVersionRequestSchema = z.strictObject(drillFields);
export type NewDrillVersionRequest = z.infer<typeof newDrillVersionRequestSchema>;

export const planStationSchema = z.strictObject({
  label: titleText,
  drillVersionId: z.uuid().nullable().default(null),
  durationSeconds: z.number().int().min(1).max(14_400),
});
export type PlanStationInput = z.infer<typeof planStationSchema>;

export const planBlockSchema = z
  .strictObject({
    objective: z.string().transform(normalizeName).pipe(z.string().min(1).max(500)),
    drillVersionId: z.uuid().nullable().default(null),
    /** Ignored when the block has parallel stations: it then lasts as long as its longest station. */
    durationSeconds: z.number().int().min(0).max(14_400).default(0),
    intensity: z.enum(intensityLevels).nullable().default(null),
    equipment: optionalText(500).default(null),
    surface: optionalText(100).default(null),
    /** Internal coaching notes; never sent to families. */
    coachNotes: optionalText(2000).default(null),
    /** Whether families see this block's summary once the plan is published and attached. */
    familyVisible: z.boolean().default(false),
    familySummary: optionalText(500).default(null),
    stations: z.array(planStationSchema).max(20).default([]),
  })
  .refine(
    (block) => !block.familyVisible || (block.familySummary !== null && block.familySummary !== ''),
  )
  .refine((block) => block.stations.length > 0 || block.durationSeconds >= 1);
export type PlanBlockInput = z.infer<typeof planBlockSchema>;

const planVersionFields = {
  title: titleText,
  objective: optionalText(1000).default(null),
  /** The length of the session the plan is designed for. */
  plannedSessionSeconds: z.number().int().min(60).max(28_800),
  preparationSeconds: z.number().int().min(0).max(14_400).default(0),
  breakSeconds: z.number().int().min(0).max(14_400).default(0),
  /** Needed to save or publish a plan whose active time does not fit its session. */
  overrideReason: optionalText(500).default(null),
  blocks: z.array(planBlockSchema).min(1).max(100),
};

export const createPlanRequestSchema = z.strictObject({
  departmentId: z.uuid(),
  ...planVersionFields,
});
export type CreatePlanRequest = z.infer<typeof createPlanRequestSchema>;

/** Replaces the content of a draft version. */
export const updatePlanVersionRequestSchema = z.strictObject(planVersionFields);
export type UpdatePlanVersionRequest = z.infer<typeof updatePlanVersionRequestSchema>;

/** Chooses the published plan version an event uses; null removes it. */
export const attachPlanRequestSchema = z.strictObject({
  trainingPlanVersionId: z.uuid().nullable(),
  /** Accepts a plan that does not fit this event's length; recorded in the audit trail. */
  overrideReason: optionalText(500).default(null),
});
export type AttachPlanRequest = z.infer<typeof attachPlanRequestSchema>;

export interface DrillView {
  id: string;
  departmentId: string | null;
  sportCode: string;
  status: 'active' | 'archived';
  /** The newest version, which new plan blocks refer to. */
  versionId: string;
  versionNumber: number;
  titles: LocalizedTitles;
  protocol: string | null;
  instructions: string | null;
  durationSeconds: number;
  equipment: string[];
}

export interface PlanStationView {
  label: string;
  drillVersionId: string | null;
  durationSeconds: number;
}

export interface PlanBlockView {
  position: number;
  objective: string;
  drillVersionId: string | null;
  /** The block's effective duration: its longest station when it has any. */
  durationSeconds: number;
  intensity: IntensityLevel | null;
  equipment: string | null;
  surface: string | null;
  coachNotes: string | null;
  familyVisible: boolean;
  familySummary: string | null;
  stations: PlanStationView[];
}

export interface PlanVersionSummaryView {
  versionId: string;
  versionNumber: number;
  publicationState: PlanPublicationState;
  title: string;
}

export interface TrainingPlanView {
  planId: string;
  departmentId: string;
  status: 'active' | 'archived';
  /** Title of the newest version. */
  title: string;
  versions: PlanVersionSummaryView[];
}

export interface PlanVersionView {
  versionId: string;
  planId: string;
  departmentId: string;
  versionNumber: number;
  title: string;
  objective: string | null;
  plannedSessionSeconds: number;
  preparationSeconds: number;
  breakSeconds: number;
  overrideReason: string | null;
  publicationState: PlanPublicationState;
  publishedAt: string | null;
  /** Counts changes to a draft; send it in `If-Match` when saving or publishing. */
  revision: number;
  activeSeconds: number;
  availableSeconds: number;
  fits: boolean;
  blocks: PlanBlockView[];
}

/** What a family sees of an attached, published plan: selected summaries only. */
export interface FamilyPlanView {
  title: string;
  blocks: { summary: string; durationMinutes: number }[];
}
